import { InputNumber, InputNumberProps, Select, Switch } from 'antd'
import CantileverRow from 'components/CantileverRow'
import { getAllItemsToRender, useScatterplotContext } from 'features/timeSeries'
import { ChartTooltip } from 'features/timeSeries/components/ChartTooltip'
import {
  FadedSpan,
  SectionContent,
  SensorControls,
  SmallText,
} from 'features/timeSeries/sharedStyles'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { BaseSensorEntry, SmoothingConfig } from 'types'

const { Option } = Select

interface DataSmoothingProps {
  selectedSensors: BaseSensorEntry[]
  sensorEntries: BaseSensorEntry[]
}

export const DataSmoothing: React.FC<DataSmoothingProps> = ({
  selectedSensors,
  sensorEntries,
}) => {
  const { smoothingConfigs, setSmoothingConfigs, derivedValues } =
    useScatterplotContext()

  const [globalSmoothing, setGlobalSmoothing] = useState<{
    enabled: boolean
    windowSize: number
    method: 'avg' | 'max' | 'min' | 'median'
  }>({
    enabled: false,
    windowSize: 5,
    method: 'avg',
  })

  // Get all items to render (sensors + derived values with dependencies met)
  const allItemsToRender = useMemo(
    () => getAllItemsToRender(selectedSensors, derivedValues, sensorEntries),
    [selectedSensors, derivedValues, sensorEntries]
  )

  // Initialize smoothing configs for selected sensors
  useEffect(() => {
    const newConfigs: Record<string, SmoothingConfig> = {}

    // Build list of all items (sensors + derived values with dependencies met)
    const allItems: Array<{ id: string }> = [...selectedSensors]

    // Add derived values that have all dependencies selected
    derivedValues.forEach((derived) => {
      const allDependenciesSelected = derived.data.dependencyIds.every(
        (sensorId) =>
          selectedSensors.some((selected) => selected.id === sensorId)
      )

      if (allDependenciesSelected) {
        allItems.push({ id: derived.id })
      }
    })

    allItems.forEach((item) => {
      if (!smoothingConfigs[item.id]) {
        newConfigs[item.id] = {
          type: 'smoothing',
          sensorId: item.id,
          enabled: false,
          windowSize: 5,
          method: 'avg',
        }
      }
    })

    if (Object.keys(newConfigs).length > 0) {
      setSmoothingConfigs((prev) => ({ ...prev, ...newConfigs }))
    }
  }, [selectedSensors, derivedValues, smoothingConfigs, setSmoothingConfigs])

  const handleSmoothingToggle = useCallback(
    (sensorId: string, enabled: boolean) => {
      const updated = {
        ...smoothingConfigs,
        [sensorId]: { ...smoothingConfigs[sensorId], enabled },
      }
      setSmoothingConfigs(updated)
    },
    [smoothingConfigs, setSmoothingConfigs]
  )

  const handleSmoothingWindowChange = useCallback(
    (sensorId: string, windowSize: number | null) => {
      const updated = {
        ...smoothingConfigs,
        [sensorId]: {
          ...smoothingConfigs[sensorId],
          windowSize: windowSize ?? 5,
        },
      }
      setSmoothingConfigs(updated)
    },
    [smoothingConfigs, setSmoothingConfigs]
  )

  const handleSmoothingMethodChange = useCallback(
    (sensorId: string, method: 'avg' | 'max' | 'min' | 'median') => {
      const updated = {
        ...smoothingConfigs,
        [sensorId]: { ...smoothingConfigs[sensorId], method },
      }
      setSmoothingConfigs(updated)
    },
    [smoothingConfigs, setSmoothingConfigs]
  )

  const handleGlobalSmoothingToggle = useCallback(
    (enabled: boolean) => {
      setGlobalSmoothing((prev) => ({ ...prev, enabled }))

      // Apply global settings to all sensors
      const updated: Record<string, SmoothingConfig> = {}
      selectedSensors.forEach((sensor) => {
        updated[sensor.id] = {
          type: 'smoothing',
          sensorId: sensor.id,
          enabled,
          windowSize: globalSmoothing.windowSize,
          method: globalSmoothing.method,
        }
      })
      setSmoothingConfigs(updated)
    },
    [
      selectedSensors,
      setSmoothingConfigs,
      globalSmoothing.windowSize,
      globalSmoothing.method,
    ]
  )

  const handleGlobalWindowChange = useCallback(
    (windowSize: InputNumberProps['value']) => {
      const size = typeof windowSize === 'number' ? windowSize : 5
      setGlobalSmoothing((prev) => ({ ...prev, windowSize: size }))

      if (globalSmoothing.enabled) {
        const updated: Record<string, SmoothingConfig> = {}
        selectedSensors.forEach((sensor) => {
          updated[sensor.id] = {
            ...smoothingConfigs[sensor.id],
            windowSize: size,
          }
        })
        setSmoothingConfigs(updated)
      }
    },
    [
      globalSmoothing.enabled,
      selectedSensors,
      setSmoothingConfigs,
      smoothingConfigs,
    ]
  )

  const handleGlobalMethodChange = useCallback(
    (method: 'avg' | 'max' | 'min' | 'median') => {
      setGlobalSmoothing((prev) => ({ ...prev, method }))

      if (globalSmoothing.enabled) {
        const updated: Record<string, SmoothingConfig> = {}
        selectedSensors.forEach((sensor) => {
          updated[sensor.id] = {
            ...smoothingConfigs[sensor.id],
            method,
          }
        })
        setSmoothingConfigs(updated)
      }
    },
    [
      globalSmoothing.enabled,
      selectedSensors,
      setSmoothingConfigs,
      smoothingConfigs,
    ]
  )

  return (
    <SectionContent>
      <SmallText type='secondary'>
        Apply moving window operations to reduce noise
      </SmallText>

      <CantileverRow
        leadingContent={<SmallText>Apply to all sensors</SmallText>}
        trailingContent={
          <Switch
            checked={globalSmoothing.enabled}
            onChange={handleGlobalSmoothingToggle}
          />
        }
      />

      {globalSmoothing.enabled && (
        <>
          <CantileverRow
            leadingContent={<SmallText>Window size</SmallText>}
            trailingContent={
              <InputNumber
                min={2}
                max={50}
                value={globalSmoothing.windowSize}
                onChange={handleGlobalWindowChange}
              />
            }
          />
          <CantileverRow
            leadingContent={<SmallText>Method</SmallText>}
            trailingContent={
              <Select
                value={globalSmoothing.method}
                onChange={handleGlobalMethodChange}>
                <Option value='avg'>Average</Option>
                <Option value='max'>Maximum</Option>
                <Option value='min'>Minimum</Option>
                <Option value='median'>Median</Option>
              </Select>
            }
          />
        </>
      )}

      {!globalSmoothing.enabled && allItemsToRender.length > 0 && (
        <SensorControls>
          {allItemsToRender.map((item) => {
            const config = smoothingConfigs[item.id]
            if (!config) return null

            return (
              <div key={item.id}>
                <CantileverRow
                  leadingContent={
                    <ChartTooltip label={item.name}>
                      <span>
                        {item.name}
                        {item.isDerived && <FadedSpan> (derived)</FadedSpan>}
                      </span>
                    </ChartTooltip>
                  }
                  trailingContent={
                    <Switch
                      checked={config.enabled}
                      onChange={(checked) =>
                        handleSmoothingToggle(item.id, checked)
                      }
                    />
                  }
                />
                {config.enabled && (
                  <>
                    <CantileverRow
                      leadingContent={<SmallText>Window</SmallText>}
                      trailingContent={
                        <InputNumber
                          min={2}
                          max={50}
                          value={config.windowSize}
                          onChange={(value) =>
                            handleSmoothingWindowChange(
                              item.id,
                              typeof value === 'number' ? value : null
                            )
                          }
                        />
                      }
                    />
                    <CantileverRow
                      tPadding='0.25rem'
                      leadingContent={<SmallText>Method</SmallText>}
                      trailingContent={
                        <Select
                          value={config.method}
                          onChange={(value) =>
                            handleSmoothingMethodChange(
                              item.id,
                              value as 'avg' | 'max' | 'min' | 'median'
                            )
                          }>
                          <Option value='avg'>Average</Option>
                          <Option value='max'>Maximum</Option>
                          <Option value='min'>Minimum</Option>
                          <Option value='median'>Median</Option>
                        </Select>
                      }
                    />
                  </>
                )}
              </div>
            )
          })}
        </SensorControls>
      )}
    </SectionContent>
  )
}
